import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    orgId: String,
    locale: String,
    translations: Object,
  };

  connect() {
    this.boundDocumentClick = this.handleDocumentClick.bind(this);
    document.addEventListener("click", this.boundDocumentClick);

    this.exposeModalFunctions();
    this.initializePage();
  }

  disconnect() {
    document.removeEventListener("click", this.boundDocumentClick);
  }

  exposeModalFunctions() {
    window.openFaqModal = (index = null, retryCount = 0) => this.openFaqModal(index, retryCount);
    window.closeFaqModal = () => this.closeFaqModal();
    window.openVideoModal = (index = null, retryCount = 0) => this.openVideoModal(index, retryCount);
    window.closeVideoModal = () => this.closeVideoModal();
    window.openArticleModal = (articleId = null, retryCount = 0) => this.openArticleModal(articleId, retryCount);
    window.closeArticleModal = () => this.closeArticleModal();
  }

  initializePage() {
    this.hideModal("faq-modal");
    this.hideModal("article-modal");
    this.hideModal("video-modal");

    this.initializeMainTabs();
    this.initializeTabs();
    this.initializeFaqHandlers();
    this.initializeAllHandlers();
  }

  initializeMainTabs() {
    const mainTabButtons = this.element.querySelectorAll(".main-tab-button");
    const mainTabContents = this.element.querySelectorAll(".main-tab-content");

    mainTabButtons.forEach((button) => {
      if (button.dataset.mainTabInitialized) {
        return;
      }

      button.dataset.mainTabInitialized = "true";
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        const targetMainTab = button.getAttribute("data-main-tab");

        mainTabButtons.forEach((btn) => {
          btn.classList.remove("ui-segmented-item-active");
          btn.setAttribute("aria-selected", "false");
        });

        mainTabContents.forEach((content) => {
          content.classList.add("hidden");
        });

        button.classList.add("ui-segmented-item-active");
        button.setAttribute("aria-selected", "true");

        const targetContent = document.getElementById(`main-tab-${targetMainTab}`);
        if (targetContent) {
          targetContent.classList.remove("hidden");
        }
      });
    });
  }

  initializeTabs() {
    const tabButtons = this.element.querySelectorAll(".tab-button");
    const tabContents = this.element.querySelectorAll(".tab-content");

    tabButtons.forEach((button) => {
      if (button.dataset.subTabInitialized) {
        return;
      }

      button.dataset.subTabInitialized = "true";
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        const targetTab = button.getAttribute("data-tab");

        tabButtons.forEach((btn) => {
          btn.classList.remove("active", "bg-slate-100", "font-semibold", "text-slate-950", "dark:bg-slate-800", "dark:text-white");
          btn.classList.add("font-medium", "text-slate-600", "dark:text-slate-300");
          btn.setAttribute("aria-selected", "false");
        });

        tabContents.forEach((content) => {
          content.classList.add("hidden");
        });

        button.classList.add("active", "bg-slate-100", "font-semibold", "text-slate-950", "dark:bg-slate-800", "dark:text-white");
        button.classList.remove("font-medium", "text-slate-600", "dark:text-slate-300");
        button.setAttribute("aria-selected", "true");

        const targetContent = document.getElementById(`tab-${targetTab}`);
        if (targetContent) {
          targetContent.classList.remove("hidden");
        }
      });
    });
  }

  handleDocumentClick(event) {
    if (event.target.classList.contains("edit-faq") || event.target.closest(".edit-faq")) {
      event.preventDefault();
      event.stopPropagation();
      const btn = event.target.classList.contains("edit-faq") ? event.target : event.target.closest(".edit-faq");
      const index = btn.dataset.faqIndex;
      if (index !== undefined) {
        this.openFaqModal(parseInt(index, 10));
      }
    } else if (event.target.classList.contains("edit-article") || event.target.closest(".edit-article")) {
      event.preventDefault();
      event.stopPropagation();
      const btn = event.target.classList.contains("edit-article") ? event.target : event.target.closest(".edit-article");
      const articleId = btn.dataset.articleId;
      if (articleId) {
        if (articleId.startsWith("new_")) {
          this.openArticleModal(articleId);
        } else {
          this.openArticleModal(parseInt(articleId, 10));
        }
      }
    } else if (event.target.classList.contains("edit-video") || event.target.closest(".edit-video")) {
      event.preventDefault();
      event.stopPropagation();
      const btn = event.target.classList.contains("edit-video") ? event.target : event.target.closest(".edit-video");
      const index = btn.dataset.videoIndex;
      if (index !== undefined) {
        this.openVideoModal(parseInt(index, 10));
      }
    } else if (event.target.classList.contains("remove-external-resource") || event.target.closest(".remove-external-resource")) {
      event.preventDefault();
      event.stopPropagation();
      const btn = event.target.classList.contains("remove-external-resource") ? event.target : event.target.closest(".remove-external-resource");
      const container = btn.closest(".external-resource-item");
      if (container) {
        container.remove();
      }
    }
  }

  openFaqModal(index = null, retryCount = 0) {
    const modal = document.getElementById("faq-modal");
    const form = document.getElementById("faq-form");
    const modalTitle = document.getElementById("faq-modal-title");

    if (!modal || !form || !modalTitle) {
      if (retryCount < 10) {
        window.setTimeout(() => this.openFaqModal(index, retryCount + 1), 100);
      }
      return;
    }

    if (index !== null) {
      const listItem = document.querySelector(`#faq-item-${index}`);
      if (listItem) {
        const questionEl = listItem.querySelector(".font-medium");
        const answerEl = listItem.querySelector(".text-sm");
        const questionInput = document.getElementById("faq-question-input");
        const answerInput = document.getElementById("faq-answer-input");
        const editIndexInput = document.getElementById("faq-edit-index");

        if (questionInput && answerInput) {
          questionInput.value = questionEl ? questionEl.textContent.trim() : "";
          answerInput.value = answerEl ? answerEl.textContent.trim() : "";
        }
        if (editIndexInput) {
          editIndexInput.value = index;
        }

        form.action = `/${this.orgIdValue}/${this.localeValue}/help_center_setting/faqs/${index}`;
        form.method = "patch";
        form.querySelector("input[name=\"_method\"]")?.remove();
        const methodInput = document.createElement("input");
        methodInput.type = "hidden";
        methodInput.name = "_method";
        methodInput.value = "patch";
        form.appendChild(methodInput);

        modalTitle.textContent = this.translationsValue.faqs.editTitle;
      }
    } else {
      const questionInput = document.getElementById("faq-question-input");
      const answerInput = document.getElementById("faq-answer-input");
      const editIndexInput = document.getElementById("faq-edit-index");

      if (questionInput && answerInput) {
        questionInput.value = "";
        answerInput.value = "";
      }
      if (editIndexInput) {
        editIndexInput.value = "";
      }

      form.action = `/${this.orgIdValue}/${this.localeValue}/help_center_setting/faqs`;
      form.method = "post";
      form.querySelector("input[name=\"_method\"]")?.remove();

      modalTitle.textContent = this.translationsValue.faqs.addTitle;
    }

    modal.classList.remove("hidden");
    modal.style.display = "";
    document.body.style.overflow = "hidden";
  }

  closeFaqModal() {
    this.hideModal("faq-modal");
  }

  openVideoModal(index = null, retryCount = 0) {
    const modal = document.getElementById("video-modal");
    const form = document.getElementById("video-form");
    const modalTitle = document.getElementById("video-modal-title");

    if (!modal || !form || !modalTitle) {
      if (retryCount < 10) {
        window.setTimeout(() => this.openVideoModal(index, retryCount + 1), 100);
      }
      return;
    }

    if (index !== null) {
      const listItem = document.querySelector(`#video-item-${index}`);
      if (listItem) {
        const titleEl = listItem.querySelector(".font-medium");
        const urlEl = listItem.querySelector(".text-sm");
        const titleInput = document.getElementById("video-title-input");
        const embedUrlInput = document.getElementById("video-embed-url-input");
        const editIndexInput = document.getElementById("video-edit-index");

        if (titleInput && embedUrlInput) {
          titleInput.value = titleEl ? titleEl.textContent.trim() : "";
          embedUrlInput.value = urlEl ? urlEl.textContent.trim() : "";
        }
        if (editIndexInput) {
          editIndexInput.value = index;
        }

        form.action = `/${this.orgIdValue}/${this.localeValue}/help_center_setting/videos/${index}`;
        form.method = "patch";
        form.querySelector("input[name=\"_method\"]")?.remove();
        const methodInput = document.createElement("input");
        methodInput.type = "hidden";
        methodInput.name = "_method";
        methodInput.value = "patch";
        form.appendChild(methodInput);

        modalTitle.textContent = this.translationsValue.videos.editTitle;
      }
    } else {
      const titleInput = document.getElementById("video-title-input");
      const embedUrlInput = document.getElementById("video-embed-url-input");
      const editIndexInput = document.getElementById("video-edit-index");

      if (titleInput && embedUrlInput) {
        titleInput.value = "";
        embedUrlInput.value = "";
      }
      if (editIndexInput) {
        editIndexInput.value = "";
      }

      form.action = `/${this.orgIdValue}/${this.localeValue}/help_center_setting/videos`;
      form.method = "post";
      form.querySelector("input[name=\"_method\"]")?.remove();

      modalTitle.textContent = this.translationsValue.videos.addTitle;
    }

    modal.classList.remove("hidden");
    modal.style.display = "";
    document.body.style.overflow = "hidden";
  }

  closeVideoModal() {
    this.hideModal("video-modal");
  }

  openArticleModal(articleId = null, retryCount = 0) {
    const modal = document.getElementById("article-modal");
    const form = document.getElementById("article-form");
    const modalTitle = document.getElementById("article-modal-title");

    if (!modal || !form || !modalTitle) {
      if (retryCount < 10) {
        window.setTimeout(() => this.openArticleModal(articleId, retryCount + 1), 100);
      }
      return;
    }

    if (articleId !== null) {
      const listItem = document.querySelector(`#article-item-${articleId}`);
      if (listItem) {
        const titleEl = listItem.querySelector(".font-medium");
        const contentEl = listItem.querySelector(".text-sm");
        const titleInput = document.getElementById("article-title-input");
        const contentInput = document.getElementById("article-content-input");
        const editIdInput = document.getElementById("article-edit-id");

        if (titleInput && contentInput) {
          titleInput.value = titleEl ? titleEl.textContent.trim() : "";
          contentInput.value = contentEl ? contentEl.textContent.trim() : "";
        }
        if (editIdInput) {
          editIdInput.value = articleId;
        }

        form.action = `/${this.orgIdValue}/${this.localeValue}/help_center_setting/articles/${articleId}`;
        form.method = "patch";
        form.querySelector("input[name=\"_method\"]")?.remove();
        const methodInput = document.createElement("input");
        methodInput.type = "hidden";
        methodInput.name = "_method";
        methodInput.value = "patch";
        form.appendChild(methodInput);

        modalTitle.textContent = this.translationsValue.articles.editTitle;

        const coverImagePreview = document.getElementById("article-cover-image-preview");
        const coverImagePreviewImg = document.getElementById("article-cover-image-preview-img");
        const existingImage = listItem.querySelector("img");
        if (existingImage && existingImage.src && !existingImage.src.includes("data:image/svg")) {
          coverImagePreviewImg.src = existingImage.src;
          coverImagePreview.classList.remove("hidden");
        } else {
          coverImagePreview.classList.add("hidden");
        }
      }
    } else {
      const titleInput = document.getElementById("article-title-input");
      const contentInput = document.getElementById("article-content-input");
      const coverImageInput = document.getElementById("article-cover-image-input");
      const coverImagePreview = document.getElementById("article-cover-image-preview");
      const editIdInput = document.getElementById("article-edit-id");

      if (titleInput && contentInput) {
        titleInput.value = "";
        contentInput.value = "";
      }
      if (coverImageInput) {
        coverImageInput.value = "";
      }
      if (coverImagePreview) {
        coverImagePreview.classList.add("hidden");
      }
      if (editIdInput) {
        editIdInput.value = "";
      }

      form.action = `/${this.orgIdValue}/${this.localeValue}/help_center_setting/articles`;
      form.method = "post";
      form.querySelector("input[name=\"_method\"]")?.remove();

      modalTitle.textContent = this.translationsValue.articles.addTitle;
    }

    modal.classList.remove("hidden");
    modal.style.display = "";
    document.body.style.overflow = "hidden";
  }

  closeArticleModal() {
    this.hideModal("article-modal");
  }

  hideModal(modalId) {
    const modal = document.getElementById(modalId);
    if (modal) {
      modal.classList.add("hidden");
      modal.style.display = "none";
    }
    document.body.style.overflow = "";
  }

  initializeFaqHandlers() {
    this.replaceClickHandler("add-faq-btn", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.openFaqModal();
    });

    this.replaceClickHandler("faq-modal-close", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.closeFaqModal();
    });

    this.replaceClickHandler("faq-modal-cancel", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.closeFaqModal();
    });

    const modal = document.getElementById("faq-modal");
    const backdrop = modal?.querySelector(".faq-modal-backdrop");
    if (backdrop && !backdrop.dataset.handlerBound) {
      backdrop.dataset.handlerBound = "true";
      backdrop.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.closeFaqModal();
      });
    }
  }

  initializeAllHandlers() {
    this.replaceClickHandler("add-article-btn", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.openArticleModal();
    });

    this.replaceClickHandler("article-modal-close", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.closeArticleModal();
    });

    this.replaceClickHandler("article-modal-cancel", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.closeArticleModal();
    });

    const articleModal = document.getElementById("article-modal");
    const articleBackdrop = articleModal?.querySelector(".article-modal-backdrop");
    if (articleBackdrop && !articleBackdrop.dataset.handlerBound) {
      articleBackdrop.dataset.handlerBound = "true";
      articleBackdrop.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.closeArticleModal();
      });
    }

    const coverImageInput = document.getElementById("article-cover-image-input");
    if (coverImageInput && !coverImageInput.dataset.handlerBound) {
      coverImageInput.dataset.handlerBound = "true";
      coverImageInput.addEventListener("change", (event) => {
        if (event.target.files && event.target.files[0]) {
          const reader = new FileReader();
          reader.onload = (loadEvent) => {
            const preview = document.getElementById("article-cover-image-preview");
            const previewImg = document.getElementById("article-cover-image-preview-img");
            if (preview && previewImg) {
              previewImg.src = loadEvent.target.result;
              preview.classList.remove("hidden");
            }
          };
          reader.readAsDataURL(event.target.files[0]);
        }
      });
    }

    this.replaceClickHandler("add-video-btn", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.openVideoModal();
    });

    this.replaceClickHandler("video-modal-close", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.closeVideoModal();
    });

    this.replaceClickHandler("video-modal-cancel", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.closeVideoModal();
    });

    const videoModal = document.getElementById("video-modal");
    const videoBackdrop = videoModal?.querySelector(".fixed.inset-0.bg-gray-500, .video-modal-backdrop");
    if (videoBackdrop && !videoBackdrop.dataset.handlerBound) {
      videoBackdrop.dataset.handlerBound = "true";
      videoBackdrop.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.closeVideoModal();
      });
    }
  }

  replaceClickHandler(elementId, handler) {
    const element = document.getElementById(elementId);
    if (!element) {
      return;
    }

    const newElement = element.cloneNode(true);
    element.parentNode.replaceChild(newElement, element);
    newElement.addEventListener("click", handler);
  }
}
